import { useEffect, useState } from "react";
import BaseViewModel from "../../base/BaseViewModel";
import Web3 from "web3";
import { etherToHex,requiredChainTip,getblockchain} from "../../utils/HelperFunctions";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  createCallbackLog,
  getOneRequest,
  sendCallback,
  updateOffering,
  updateOneRequest,
} from "../../utils/Apis";
import { useWalletState } from "../../utils/Hooks/useWalletState";
import { useWeb3Handler } from "../../utils/Hooks/useWeb3Handler";

const { ethereum } = window;
const web3 = new Web3(ethereum);

function usePlaceBid() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    address,
    requestAccounts,
    isChainValid,
    requiredChain,
    currentChain,
  } = useWalletState();
  const { sendTransaction } = useWeb3Handler(web3);

  const [isLoading, setIsLoading] = useState(false);
  const [request, setRequest] = useState({});
  const [error, setError] = useState(false);
  const [alert, setAlert] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [offering, setOffering] = useState({});
  const [source, setSource] = useState("");
  const [contract, setContract] = useState({});

  const fetchRequest = async () => {
    setIsLoading(true);
    try {
      const ref = searchParams.get("ref");
      if (!ref) {
        navigate("/error/?type=invalid-reference", { replace: true });
        return;
      }
      const { message } = await getOneRequest(ref);
      const {
        callbackUrl = "",
        status = "pending",
        _id: id,
        transactions = [],
        Offering,
        bid,
        externalRef,
      } = message || {};
      if (status === "success")
        navigate("/success", { state: { source: externalRef }, replace: true });
      if (!["pending", "active"].includes(Offering.status)) {
        navigate("/error?tips=Invalid offering.", {
          state: { source },
          replace: true,
        });
        return;
      }
      const { MarketplaceContract } = Offering || {};
      setContract(MarketplaceContract);
      setSource(externalRef);
      setRequest({
        callbackUrl,
        status,
        ref,
        id,
        transactions,
        bid,
      });
      setOffering(Offering);
    } catch (e) {
      console.error(e);
      navigate("/error", { state: { source }, replace: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOffering = async () => {
    const { status, _id } = offering;
    if (status === "pending") {
      await updateOffering(_id, { status: "active" });
    }
  };

  const handleCallback = async (callbackData, requestData) => {
    setIsLoading(true);
    let status;
    try {
      await updateOneRequest(request.ref, requestData);
      status = "success";
      if (requestData.status === "success") await handleOffering();
      await sendCallback(request.callbackUrl, callbackData);
      await createCallbackLog({
        Request: request.id,
        status: "success",
        rawData: callbackData,
      });
    } catch (e) {
      await createCallbackLog({
        Request: request.id,
        status: "failed",
        rawData: callbackData,
      });
      console.error(`Failed to update offering: ${e.message}`);
    } finally {
      setIsLoading(false);
      if (requestData.status === "success" && status === "success")
        navigate("/success", { state: { source }, replace: true });
    }
  };

  const placeBid = async () => {
    const { bid } = request || {};
    return sendTransaction({
      contract,
      methodName: "placeBid",
      inputs: [offering.ref],
      from: address,
      action: "PlaceBid",
      value: etherToHex(bid),
    });
  };

  const onConfirm = async () => {
    try {
      if (request.status === "success") return;

      setError(false);
      setIsLoading(true);

      const transactions = [...request.transactions];

      const { seller = "" } = offering.details || {};
      if (seller.toLowerCase() === address?.toLowerCase()) {
        setAlert({
          type: "error",
          title: "Message",
          message: "You cannot bid your own products",
        });
        setShowAlert(true);
        return;
      }

      let txResult = await placeBid();

      transactions.push(txResult);

      if (!txResult.status) setError(true);

      const response = {
        status: txResult.status ? "success" : "failed",
        transactions,
      };

      await handleCallback(
        {
          ...response,
          ref: request.ref,
        },
        response
      );
    } catch (e) {
      window.alert(e.message);
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const onCloseAlert = () => {
    setAlert({});
    setShowAlert(false);
  };

  useEffect(() => {
    if (!address) {
      setAlert({
        type: "error",
        title: "Error",
        message: `Please connect to your wallet`,
      });
      setShowAlert(true);
    }
  }, [address]);

  useEffect(() => {
    if (!isChainValid) {
      if(getblockchain()=='Polygon'){
        setShowAlert(false);
        requiredChainTip();
     }else{
      setAlert({
        type: "error",
        title: "Error",
        message: `You are using service provided on ${requiredChain}, but your wallet is connected to ${currentChain}.`,
      });
      setShowAlert(true);
     }
     
    }else{
      if(document.querySelector('.login_tip_dialog')){
        document.querySelector('.login_tip_dialog').remove();
      } 
    }
  }, [isChainValid]);


  useEffect(() => {
    fetchRequest();
  }, []);

  return {
    isLoading,
    error,
    alert,
    showAlert,
    address,
    isChainValid,

    onConfirm,
    onCloseAlert,
    requestAccounts,
  };
}

const PlaceBidViewModel = BaseViewModel(usePlaceBid);

export default PlaceBidViewModel;
